import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import type {
  AssignedClient,
  TeamFieldValidation,
  TeamMember,
  TeamRoleField,
} from '../api/teamApi';

export function getTeamMemberName(member: Record<string, unknown>) {
  return (
    [member.firstName, member.lastName].filter(Boolean).join(' ') ||
    'Team member'
  );
}

export function getTeamMemberInitials(member: Record<string, unknown>) {
  const parts = getTeamMemberName(member).split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || 'T'}${parts[1]?.[0] || ''}`.toUpperCase();
}

/**
 * Resolves a display status from team-members API shapes
 * (`status`, `accountStatus`, `disableUser`, `isActive`, etc.).
 */
export function resolveTeamMemberStatus(
  member: Record<string, unknown>,
): string {
  const raw =
    member.status ??
    member.accountStatus ??
    member.userStatus ??
    member.verificationStatus ??
    member.memberStatus;

  if (typeof raw === 'string' && raw.trim()) {
    const s = raw.trim().toUpperCase();
    if (s === 'UNKNOWN' || s === 'UNDEFINED' || s === 'NULL') {
      // fall through to boolean flags
    } else {
      return s;
    }
  }

  if (member.disableUser === true || member.isDisabled === true) {
    return 'DISABLED';
  }
  if (member.isActive === false || member.active === false) {
    return 'INACTIVE';
  }
  if (member.isVerified === true || member.isAdminVerified === true) {
    return 'VERIFIED';
  }
  if (member.isActive === true || member.active === true) {
    return 'ACTIVE';
  }
  if (member.disableUser === false || member.isDisabled === false) {
    return 'ACTIVE';
  }

  return 'ACTIVE';
}

export function getTeamMemberStatusMeta(status?: string) {
  const s = String(status || '').toUpperCase();

  if (s === 'VERIFIED' || s === 'ACTIVE' || s === 'ENABLED') {
    return {
      label: s === 'VERIFIED' ? 'Verified' : 'Active',
      bg: 'bg-[#ECFDF5]',
      text: 'text-[#297857]',
      dot: 'bg-[#297857]',
    };
  }
  if (s === 'DISABLED' || s === 'INACTIVE' || s === 'BLOCKED') {
    return {
      label: s === 'DISABLED' ? 'Disabled' : 'Inactive',
      bg: 'bg-[#FEF2F2]',
      text: 'text-[#B91C1C]',
      dot: 'bg-[#EF4444]',
    };
  }
  if (s === 'PENDING' || s === 'INVITED' || s === 'UNVERIFIED') {
    return {
      label: s === 'INVITED' ? 'Invited' : 'Pending',
      bg: 'bg-[#FFFBEB]',
      text: 'text-[#B45309]',
      dot: 'bg-[#F59E0B]',
    };
  }
  if (!s) {
    return {
      label: 'Active',
      bg: 'bg-[#ECFDF5]',
      text: 'text-[#297857]',
      dot: 'bg-[#297857]',
    };
  }
  return {
    label: s.charAt(0) + s.slice(1).toLowerCase(),
    bg: 'bg-[#FFFBEB]',
    text: 'text-[#B45309]',
    dot: 'bg-[#F59E0B]',
  };
}

export function normalizeTeamMemberList(data: unknown): TeamMember[] {
  const payload = (data as { data?: unknown })?.data;
  let list: TeamMember[] = [];
  if (Array.isArray(payload)) list = payload as TeamMember[];
  else {
    const users = (payload as { users?: unknown })?.users;
    if (Array.isArray(users)) list = users as TeamMember[];
    else {
      const members = (payload as { members?: unknown })?.members;
      if (Array.isArray(members)) list = members as TeamMember[];
    }
  }

  return list.map((member) => {
    const row = member as Record<string, unknown>;
    return {
      ...member,
      status: resolveTeamMemberStatus(row),
    };
  });
}

export function normalizeAssignedClientList(data: unknown): AssignedClient[] {
  const payload = (data as { data?: unknown })?.data ?? data;
  if (Array.isArray(payload)) return payload as AssignedClient[];
  if (payload && typeof payload === 'object') {
    const obj = payload as {
      clients?: unknown;
      users?: unknown;
      assignedClients?: unknown;
    };
    if (Array.isArray(obj.clients)) return obj.clients as AssignedClient[];
    if (Array.isArray(obj.users)) return obj.users as AssignedClient[];
    if (Array.isArray(obj.assignedClients)) {
      return obj.assignedClients as AssignedClient[];
    }
  }
  return [];
}

/** Maps create-fields form keys onto the create API body shape. */
export function buildTeamCreatePayload(
  formData: Record<string, unknown>,
  mobileCountryCode?: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  Object.entries(formData).forEach(([key, value]) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    const normalizedKey = key.replace(/[\s_-]+/g, '').toLowerCase();

    if (normalizedKey === 'firstname') payload.firstName = trimmed;
    else if (normalizedKey === 'lastname') payload.lastName = trimmed;
    else if (normalizedKey === 'email') payload.email = trimmed;
    else if (
      normalizedKey === 'mobilenumber' ||
      normalizedKey === 'phone' ||
      normalizedKey === 'phonenumber'
    ) {
      payload.mobileNumber = trimmed;
    } else if (
      normalizedKey === 'pannumber' ||
      normalizedKey === 'pan'
    ) {
      payload.panNumber = trimmed;
    } else if (
      normalizedKey === 'gstnumber' ||
      normalizedKey === 'gst'
    ) {
      payload.gstNumber = trimmed;
    } else if (
      normalizedKey.includes('aadhaar') ||
      normalizedKey.includes('aadhar')
    ) {
      payload.aadharNumber = trimmed;
      payload.aadharCard = trimmed;
    } else if (
      normalizedKey === 'companyname' ||
      normalizedKey === 'company' ||
      normalizedKey === 'entityname'
    ) {
      payload.companyName = trimmed;
    } else if (normalizedKey === 'address' || normalizedKey === 'fulladdress') {
      payload.address = trimmed;
    } else {
      payload[key] = trimmed;
    }
  });

  if (mobileCountryCode && payload.mobileNumber) {
    payload.mobileCountryCode = mobileCountryCode;
  }

  // Create is without clients or module permissions; welcome email is sent by default.
  payload.sendWelcomeEmail = true;
  // Explicitly omit — team members get access via assigned clients, not permission checkboxes.
  delete payload.permissions;

  return payload;
}

/** Profile-only update payload (no email / welcome email / clients). */
export function buildTeamUpdatePayload(
  formData: Record<string, unknown>,
  options?: { mobileCountryCode?: string; disableUser?: boolean },
): Record<string, unknown> {
  const payload = buildTeamCreatePayload(formData, options?.mobileCountryCode);
  delete payload.email;
  delete payload.sendWelcomeEmail;
  payload.disableUser = Boolean(options?.disableUser);
  return payload;
}

/** Unwraps `GET team-members/:id` into a flat member object. */
export function extractTeamMemberData(response: unknown): Record<string, unknown> {
  const root = (response as { data?: unknown })?.data ?? response;
  if (!root || typeof root !== 'object') return {};
  const obj = root as Record<string, unknown>;
  if (obj.user && typeof obj.user === 'object') {
    return { ...obj, ...(obj.user as Record<string, unknown>) };
  }
  if (obj.member && typeof obj.member === 'object') {
    return { ...obj, ...(obj.member as Record<string, unknown>) };
  }
  return obj;
}

function toDisplayString(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const parts = [
      obj.fullAddress,
      obj.address,
      obj.line1,
      obj.addressLine1,
      obj.street,
      obj.city,
      obj.state,
      obj.pin,
      obj.pincode,
    ].filter((v) => typeof v === 'string' && v.trim());
    return parts.map(String).join(', ');
  }
  return '';
}

function readMemberField(
  member: Record<string, unknown>,
  formKey: string,
): string {
  const direct = toDisplayString(member[formKey]);
  if (direct) return direct;

  const aliases: Record<string, string[]> = {
    firstName: ['firstName', 'first_name', 'First Name'],
    lastName: ['lastName', 'last_name', 'Last Name'],
    email: ['email', 'emailAddress', 'Email Address'],
    mobileNumber: [
      'mobileNumber',
      'mobile',
      'phone',
      'phoneNumber',
      'Mobile Number',
    ],
    panNumber: ['panNumber', 'pan', 'pan_number', 'PAN Number', 'PAN'],
    gstNumber: ['gstNumber', 'gst', 'gst_number', 'GST Number', 'GST'],
    aadharNumber: [
      'aadharNumber',
      'aadhaarNumber',
      'aadhar',
      'aadhaar',
      'aadharCard',
      'Aadhaar Number',
      'Aadhain apiar Number',
    ],
    companyName: ['companyName', 'entityName', 'company', 'Company Name'],
    address: ['address', 'fullAddress', 'Address'],
  };

  for (const alias of aliases[formKey] || []) {
    const value = toDisplayString(member[alias]);
    if (value) return value;
  }

  // Case-insensitive scan (skip nested objects / arrays)
  const want = formKey.replace(/[\s_-]+/g, '').toLowerCase();
  for (const [key, value] of Object.entries(member)) {
    if (key.replace(/[\s_-]+/g, '').toLowerCase() !== want) continue;
    const text = toDisplayString(value);
    if (text) return text;
  }

  return '';
}

/** Builds RHF default values for create-fields keys from member detail. */
export function buildTeamFormDefaults(
  fields: TeamRoleField[],
  member: Record<string, unknown>,
): Record<string, string> {
  const defaults: Record<string, string> = {};
  fields.forEach((field) => {
    const name = field.fieldId?.fieldName;
    if (!name) return;
    defaults[name] = readMemberField(member, name);
  });
  return defaults;
}

/** Display rows for detail page — only create-fields keys, with labels from the API. */
export function buildTeamDetailRows(
  fields: TeamRoleField[],
  member: Record<string, unknown>,
  options?: { mobileCountryCode?: string },
): Array<{ key: string; label: string; value: string }> {
  return fields
    .map((field) => {
      const name = field.fieldId?.fieldName;
      if (!name || field.visible === false) return null;
      const label = field.fieldId?.fieldLabel || name;
      let value = readMemberField(member, name);

      const key = name.replace(/[\s_-]+/g, '').toLowerCase();
      if (
        (key === 'mobilenumber' || key === 'phone' || key === 'phonenumber') &&
        value
      ) {
        value =
          formatMobileWithCountryCode(
            value,
            options?.mobileCountryCode ||
              String(member.mobileCountryCode || ''),
          ) || value;
      }

      return { key: name, label, value };
    })
    .filter((row): row is { key: string; label: string; value: string } =>
      Boolean(row),
    );
}

type RawCreateField = {
  _id?: string;
  visible?: boolean;
  required?: boolean;
  fieldName?: string;
  fieldLabel?: string;
  fieldType?: string;
  placeholder?: string;
  validation?: TeamFieldValidation;
  fieldId?: TeamRoleField['fieldId'];
  field?: TeamRoleField['fieldId'];
};

/** Meta fields that must not render (handled automatically / not form inputs). */
const TEAM_CREATE_HIDDEN_FIELDS = new Set([
  'sendwelcomeemail',
  'welcomeemail',
  'mobilecountrycode',
  'countrycode',
  'country',
]);

function shouldHideTeamCreateField(fieldName: string, fieldLabel?: string) {
  const nameKey = fieldName.replace(/[\s_-]+/g, '').toLowerCase();
  if (TEAM_CREATE_HIDDEN_FIELDS.has(nameKey)) return true;

  const labelKey = (fieldLabel || '').replace(/[\s_-]+/g, '').toLowerCase();
  return (
    labelKey.includes('welcomeemail') ||
    labelKey.includes('countrycode') ||
    labelKey === 'country'
  );
}

function canonicalFormKey(fieldName: string, fieldLabel?: string): string {
  const key = fieldName.replace(/[\s_-]+/g, '').toLowerCase();
  const label = (fieldLabel || '').replace(/[\s_-]+/g, '').toLowerCase();
  if (
    key.includes('aadhaar') ||
    key.includes('aadhar') ||
    label.includes('aadhaar') ||
    label.includes('aadhar')
  ) {
    return 'aadharnumber';
  }
  if (key.includes('gst') || label.includes('gst')) return 'gstnumber';
  if (
    (key.includes('pan') || label.includes('pan')) &&
    !key.includes('company') &&
    !label.includes('company')
  ) {
    return 'pannumber';
  }
  if (key === 'emailaddress') return 'email';
  if (key === 'phone' || key === 'phonenumber' || key === 'mobile') {
    return 'mobilenumber';
  }
  if (key === 'company' || key === 'entityname') return 'companyname';
  return key;
}

/**
 * Maps API field labels like "PAN Number" / "First Name" to safe RHF keys
 * (spaces break Controller value ↔ error wiring).
 */
export function toTeamFormFieldName(
  fieldName: string,
  fieldType?: string,
  fieldLabel?: string,
): string {
  const key = fieldName.replace(/[\s_-]+/g, '').toLowerCase();
  const type = (fieldType || '').replace(/[\s_-]+/g, '').toLowerCase();
  const label = (fieldLabel || '').replace(/[\s_-]+/g, '').toLowerCase();

  if (key === 'firstname') return 'firstName';
  if (key === 'lastname') return 'lastName';
  if (key === 'email' || key === 'emailaddress') return 'email';
  if (
    key === 'mobilenumber' ||
    key === 'phone' ||
    key === 'phonenumber' ||
    key === 'mobile'
  ) {
    return 'mobileNumber';
  }
  if (
    key.includes('aadhaar') ||
    key.includes('aadhar') ||
    type.includes('aadhaar') ||
    type.includes('aadhar') ||
    label.includes('aadhaar') ||
    label.includes('aadhar')
  ) {
    return 'aadharNumber';
  }
  if (
    key === 'pannumber' ||
    key === 'pan' ||
    type === 'pan_number' ||
    type === 'pannumber' ||
    label === 'pannumber'
  ) {
    return 'panNumber';
  }
  if (
    key === 'gstnumber' ||
    key === 'gst' ||
    type === 'gst_number' ||
    type === 'gstnumber' ||
    label === 'gstnumber'
  ) {
    return 'gstNumber';
  }
  if (key === 'companyname' || key === 'company' || key === 'entityname') {
    return 'companyName';
  }
  if (key === 'address' || key === 'fulladdress') return 'address';

  // Fallback: camelCase without spaces/symbols
  const cleaned = fieldName.replace(/[^a-zA-Z0-9]+/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return fieldName;
  return (
    parts[0].charAt(0).toLowerCase() +
    parts[0].slice(1) +
    parts
      .slice(1)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join('')
  );
}

/**
 * Normalizes `GET team-members/create-fields` into the shape `DynamicProfileFields` expects.
 * Shows all API fields (incl. Aadhaar / company / address), dedupes PAN/GST, hides meta-only fields.
 */
export function normalizeTeamCreateFields(data: unknown): TeamRoleField[] {
  const payload = (data as { data?: unknown })?.data ?? data;
  const rawList: unknown[] = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { selectedFields?: unknown })?.selectedFields)
      ? (payload as { selectedFields: unknown[] }).selectedFields
      : Array.isArray((payload as { fields?: unknown })?.fields)
        ? (payload as { fields: unknown[] }).fields
        : [];

  const mapped = rawList
    .map((entry, index): TeamRoleField | null => {
      if (!entry || typeof entry !== 'object') return null;
      const item = entry as RawCreateField;
      const nested = item.fieldId || item.field;
      const rawName = nested?.fieldName || item.fieldName;
      if (!rawName) return null;

      const fieldLabel = nested?.fieldLabel || item.fieldLabel || rawName;
      if (shouldHideTeamCreateField(rawName, fieldLabel)) return null;

      const fieldType = nested?.fieldType || item.fieldType || 'text';
      const fieldName = toTeamFormFieldName(rawName, fieldType, fieldLabel);

      return {
        _id: item._id || fieldName || String(index),
        visible: item.visible !== false,
        required: Boolean(item.required),
        fieldId: {
          fieldName,
          fieldLabel,
          fieldType,
          placeholder: nested?.placeholder || item.placeholder,
          validation: nested?.validation || item.validation,
        },
      };
    })
    .filter((field): field is TeamRoleField => Boolean(field));

  // Collapse duplicates (e.g. "PAN Number" + "panNumber").
  const byKey = new Map<string, TeamRoleField>();
  mapped.forEach((field) => {
    const key = canonicalFormKey(
      field.fieldId?.fieldName || '',
      field.fieldId?.fieldLabel,
    );
    if (!key) return;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, field);
      return;
    }
    const existingScore =
      (existing.required ? 2 : 0) +
      (existing.fieldId?.validation?.pattern ? 2 : 0) +
      (existing.fieldId?.validation ? 1 : 0);
    const nextScore =
      (field.required ? 2 : 0) +
      (field.fieldId?.validation?.pattern ? 2 : 0) +
      (field.fieldId?.validation ? 1 : 0);
    if (nextScore >= existingScore) byKey.set(key, field);
  });

  const preferredOrder = [
    'firstname',
    'lastname',
    'email',
    'mobilenumber',
    'pannumber',
    'gstnumber',
    'aadharnumber',
    'companyname',
    'address',
  ];

  const ordered: TeamRoleField[] = [];
  preferredOrder.forEach((key) => {
    const field = byKey.get(key);
    if (field) {
      ordered.push(field);
      byKey.delete(key);
    }
  });
  byKey.forEach((field) => ordered.push(field));
  return ordered;
}
